/* eslint-disable */
import { FC, useEffect, useState } from 'react';
import { Input, Select, Tag, Typography } from 'antd';
import Alert from '~/components/Alert';
import dayjs from 'dayjs';
import DateInput from '~/components/inputs/DateInput';
import DatePicker from '~/components/DatePicker';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Form, LegalTypeSelect } from '~/components';
import WizardForm from '~/components/WizardForm';
import { useGetApplication, useGetPayloadWithProvided } from '~/controllers/wizard';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { Draft } from '~/types/Draft';
import { KsaCompanyInformation } from '~/types/ksa/KsaCompanyInformation';
import buildNamePath from '~/utils/buildNamePath';
import Section from '~/layouts/Section ';
import { Stage } from '~/types/Application';
import StepReviewComment from '~/pages/ksa/review/components/StepReviewComment';
import { handleMccGroupChangeKsa } from '~/utils/handleCompanyType';
import { useHandbook } from '~/controllers/handbook';
import { isEnglish } from '~/utils/isEnglish';
import { useGetlocalizedValue } from '~/hooks/useGetlocalizedValue';
import ReviewItem from '../components/ReviewItem';
import CalendarTypeField from '../components/CalendarTypeField';
import LegalTypeField from '../components/LegalTypeField';
import MccCodeSelect from '../../application/components/MccCodeSelect';
import { detectFieldIssue } from '~/utils/detectFieldIssue';
import { hasArabicChars } from '~/utils/containsArabic';
import { validateWebsite } from '~/utils/urlValidation';
import AgentMissingValueLabel from '~/components/AgentMissingValueLabel';

import useNationalAddressFormSync from '~/hooks/useNationalAddressFormSync';
import { useGetDraft } from '~/controllers/draft';
import DocumentInput from '~/components/inputs/DocumentInput';
import {
  mergeNationalAddress, missingNationalAddressFields, nationalAddressReviewRequired,
  savedSplAddress, validateNationalAddressSubmission,
} from '~/utils/nationalAddress';

const n = buildNamePath('reviewCompanyInformation');
const p = buildNamePath('companyInformation');
const nr = buildNamePath('reviewRiskQuestionnaire');


/** Validates that the number contains only digits, spaces, +, -, and parentheses — no letters */
const validatePhoneNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();
  const regex = /^[0-9\s\+\-\(\)]+$/;
  if (!regex.test(value)) {
    return Promise.reject('Must contain only digits and may include +, -, spaces, or parentheses');
  }
  return Promise.resolve();
};

/** Normalises any truthy/falsy ecommerce value to a consistent 'yes' | 'no' string
 *  so that the Select option values (strings) always match.
 *  Handles: boolean true/false (from API), 'yes'/'no' (saved draft), 'Yes'/'No' (legacy). */
function normalizeEcommerce(val: any): 'yes' | 'no' | undefined {
  if (val === true || val === 'yes' || val === 'Yes') return 'yes';
  if (val === false || val === 'no' || val === 'No') return 'no';
  return undefined;
}

const PAGE_SIZE = 3;


const CompanyInformation: FC = () => {
  const { t } = useTranslation('ksaReview');

  const [form] = WizardForm.useForm();
  const [optionPage, setOptionPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const taxRegistration = WizardForm.useWatch(n(['taxRegistrationCertificate', 'enabled']), form);
  useEffect(() => {
    form.setFields([
      { name: n(['taxRegistrationCertificate', 'number']), value: taxRegistration },
      { name: n(['taxRegistrationCertificate', 'files']), value: taxRegistration },
    ]);
  }, [taxRegistration, form]);

  const handbook = useHandbook()?.data?.handbooks;

  const getRqLocalizedValue = (catKey: string, apiValue?: string): string | undefined => {
    if (!apiValue) return undefined;
    return (
      handbook
        ?.find((entry) => entry.key === catKey)
        ?.values?.find((v: any) => v.apiValue === apiValue)?.localizedValue ?? apiValue
    );
  };

  const onValuesChange = (changedValues: KsaCompanyInformation, formValues: any) => {
    handleMccGroupChangeKsa(form, handbook, p)(changedValues, formValues);
  };

  const payload: any = useGetPayloadWithProvided();
  const applicationQuery = useGetApplication();

  // When the stage is ksaContract the company information is locked — all agent-update
  // fields that are not strictly needed at this stage should be disabled.
  const isKsaContract = applicationQuery.data?.stage === Stage.ksaContract;

  const companyInformation: any = payload?.companyInformation as KsaCompanyInformation;
  const riskQuestionnaire: any = payload?.riskQuestionnaire;
  const thiqahFullData: any = payload?.thiqahFullInfo;
  const elmInfo: any = payload?.elmInfo;
  const applicantDetails: any = payload?.applicantDetails;
  const businessDetails: any = companyInformation?.businessDetails ?? {};

  const draftQuery = useGetDraft<Draft<'reviewCompanyInformation'>>();
  const splAddress = savedSplAddress(payload);
  const currentAddress = WizardForm.useWatch(n(['nationalAddress']), form);
  const effectiveAddress = mergeNationalAddress(
    splAddress, payload?.reviewCompanyInformation?.nationalAddress,
    currentAddress === undefined
      ? draftQuery.data?.reviewCompanyInformation?.nationalAddress : currentAddress,
  );
  const requiresAddressReview = nationalAddressReviewRequired(payload) ||
    missingNationalAddressFields(effectiveAddress).length > 0;

  useNationalAddressFormSync(
    form,
    splAddress,
    payload?.reviewCompanyInformation?.nationalAddress,
    !applicationQuery.isLoading && (!draftQuery.isLoading || !!draftQuery.data),
  );

  const isEcomProduct = !!(payload?.services as any)?.eCommerce?.enabled;
  useEffect(() => {
    // Priority: saved agent value (agentIsEcommerce) → API field (hasEcommerce)
    const raw = payload?.reviewCompanyInformation?.agentIsEcommerce ?? payload?.thiqahFullInfo?.hasEcommerce;
    const normalized = normalizeEcommerce(raw);
    if (normalized !== undefined) {
      form.setFields([{ name: n(['agentIsEcommerce']), value: normalized }]);
    }
  }, [payload?.thiqahFullInfo?.hasEcommerce, payload?.reviewCompanyInformation?.agentIsEcommerce, form]);
  if (!payload) return null;
  const capital = payload?.thiqahFullInfo?.capital;

  const isic = payload?.thiqahFullInfo?.activities?.isic;

  const allOptions: string[] = (isic ?? thiqahFullData?.activities ?? []).map((item: any) => item.name);
  const filteredOptions: string[] = searchQuery.trim()
    ? allOptions.filter((opt) => opt.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : allOptions;
  const totalOptionPages = Math.ceil(filteredOptions.length / PAGE_SIZE);
  const visibleOptions = filteredOptions.slice(optionPage * PAGE_SIZE, optionPage * PAGE_SIZE + PAGE_SIZE);

  const isEcommerce = normalizeEcommerce(
    payload?.reviewCompanyInformation?.agentIsEcommerce ?? payload?.thiqahFullInfo?.hasEcommerce
  );

  return (
    <Container className="CompanyInformation">
      <WizardForm<Draft<'reviewCompanyInformation'>>
        stage={Stage.ksaReview}
        payloadKey="reviewCompanyInformation"
        form={form}
        setInitialValues={(values) => ({
          ...values,
          reviewCompanyInformation: {
            ...values.reviewCompanyInformation,
            nationalAddress: mergeNationalAddress(
              splAddress, payload?.reviewCompanyInformation?.nationalAddress,
              values.reviewCompanyInformation?.nationalAddress,
            ),
          },
        })}
        onBeforeFinish={(values) => validateNationalAddressSubmission(
          form,
          mergeNationalAddress(splAddress, payload?.reviewCompanyInformation?.nationalAddress,
            values.reviewCompanyInformation?.nationalAddress),
          t('Complete mandatory national address fields before approval.'),
        )}
      // onValuesChange={onValuesChange}
      >
        <Typography.Title>{t('Company Information')}</Typography.Title>

        <Typography.Title level={4}>{t('Company details').toUpperCase()}</Typography.Title>
        <Section formItemLast>
          <InputsGroupLayout>
            <ReviewItem label={t('Company legal name')}>
              {thiqahFullData?.name}
              <AgentMissingValueLabel issue={detectFieldIssue(companyInformation?.legalName)} />
            </ReviewItem>
            <Form.Item
              label={t('Company legal name – Agent Updates')}
              name={n(['englishLegalName'])}
              initialValue={
                isEnglish(companyInformation?.legalName) ? companyInformation?.legalName : undefined
              }
              disabled={isKsaContract}
              rules={
                !isKsaContract && detectFieldIssue(companyInformation?.legalName)
                  ? [
                    { required: true, message: t('Please enter the English company legal name') },
                    {
                      validator: (_: any, value: string) => {
                        if (value && hasArabicChars(value)) {
                          return Promise.reject(t('Arabic letters are not allowed'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
                  : []
              }
            >
              <Input disabled={isKsaContract || !detectFieldIssue(companyInformation?.legalName)} />
            </Form.Item>
            {/* Company Commercial Name [En] — source changed from Thiqah to merchantNameEnglish from SOB (businessDetails) */}
            <ReviewItem label={t('Company Commercial Name [En]')}>
              {businessDetails?.merchantNameEnglish}
            </ReviewItem>

            {/* Company Commercial Name [Ar] — NEW field, source: merchantNameArabic from SOB */}
            <ReviewItem label={t('Company Commercial Name [Ar]')}>
              {businessDetails?.merchantNameArabic}
            </ReviewItem>

            {/* Company Commercial Name [En] – Agent Updates — ALWAYS editable, feeds BE/screening systems */}
            <Form.Item
              labelText={t('Company Commercial Name [En] – Agent Updates')}
              name={n(['englishCommercialName'])}
              initialValue={businessDetails?.merchantNameEnglish || undefined}
              disabled={isKsaContract}
              rules={
                isKsaContract
                  ? []
                  : [
                    { required: true, message: t('Please enter the English company commercial name') },
                    {
                      validator: (_: any, value: string) => {
                        const hasArabicChars = (text: string) => /[\u0600-\u06FF]/.test(text);
                        if (value && hasArabicChars(value)) {
                          return Promise.reject(t('Arabic letters are not allowed'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
              }
            >
              <Input disabled={isKsaContract} placeholder={t('Enter English commercial name')} />
            </Form.Item>

            {/* Company Commercial Name [Ar] – Agent Updates — NEW editable field */}
            <Form.Item
              labelText={t('Company Commercial Name [Ar] – Agent Updates')}
              name={n(['arabicCommercialName'])}
              initialValue={businessDetails?.merchantNameArabic || undefined}
              disabled={isKsaContract}
              rules={
                isKsaContract
                  ? []
                  : [
                    {
                      validator: (_: any, value: string) => {
                        if (value && /[a-zA-Z]/.test(value)) {
                          return Promise.reject(t('English letters are not allowed'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
              }
            >
              <Input disabled={isKsaContract} placeholder={t('Enter Arabic commercial name')} />
            </Form.Item>
            {/* Company Legal Type display — read-only from Thiqah */}
            <ReviewItem label={t('Company legal type')}>
              <LegalTypeField value={companyInformation?.legalType} />
            </ReviewItem>

            <ReviewItem label={t('Form Name')}>{thiqahFullData?.entityType?.formName}</ReviewItem>
            <IndentedSection>
              <SubSectionTitle level={5}>
                {t('Company Characters')}
              </SubSectionTitle>
              {thiqahFullData?.entityType?.characters?.map((item: any) => {
                return <Tag key={item}>{item?.name}</Tag>;
              })}
            </IndentedSection>
            {/* <ReviewItem label={t('is License Based')}> */}
            {/*   {thiqahFullData?.licenseBased ? 'Yes' : 'No'} */}
            {/* </ReviewItem> */}
            <ReviewItem label={t('License Issuer Name')}>
              {thiqahFullData?.licenseIssuerName}
            </ReviewItem>
            <ReviewItem label={t('Partners Nationality Name')}>
              {thiqahFullData?.partnersNationalityName}
            </ReviewItem>
            <ReviewItem label={t('Is Main')}>{thiqahFullData?.main ? 'Yes' : 'No'}</ReviewItem>
            <ReviewItem label={t('Parent Company License Number')}>
              {companyInformation?.parentCompanyLicenseNumber}
            </ReviewItem>

            <ReviewItem label={t('Main Cr National Number')}>
              {thiqahFullData?.mainCrNumber}
            </ReviewItem>

            <ReviewItem label={t('In Liquidation Process')}>
              {thiqahFullData?.inLiquidationProcess ? 'Yes' : 'No'}
            </ReviewItem>
            <ReviewItem label={t('Commercial registration number')}>
              {thiqahFullData?.crNumber}
            </ReviewItem>
            <ReviewItem label={t('Commercial entity number')}>
              {thiqahFullData?.crNationalNumber}
            </ReviewItem>
            {/* CR Status is not returned by Thiqah so it is always a manual agent entry */}
            <Form.Item
              labelText={t('CR Status – Agent Updates')}
              name={n(['agentCrStatus'])}
              initialValue={thiqahFullData?.status?.name || undefined}
              disabled={isKsaContract}
              rules={[{ required: true, message: t('Please enter CR status') }]}
            >
              <Input disabled={isKsaContract} placeholder={t('Enter CR status')} />
            </Form.Item>
            <ReviewItem label={t('Date of Establishment')}>
              {companyInformation?.dateOfEstablishment}
            </ReviewItem>
            <ReviewItem label={t('License issue date')} format="date">
              {thiqahFullData?.issueDateGregorian}
            </ReviewItem>
            {/* Confirmation Date maps to thiqahFullInfo.confirmationDate.gregorian */}
            <Form.Item
              labelText={t('Confirmation Date – Agent Updates')}
              name={n(['agentConfirmationDate'])}
              initialValue={thiqahFullData?.status?.confirmationDate?.gregorian || undefined}
              disabled={isKsaContract}
            >
              <DateInput disabled={isKsaContract} />
            </Form.Item>
            <ReviewItem label={t('Company Period [In Years]')}>
              {thiqahFullData?.companyDuration}
            </ReviewItem>
            <ReviewItem label={t('Reactivation Date')} format="date">
              {thiqahFullData?.status?.reactivationDate?.gregorian}
            </ReviewItem>
            {/* Risk Team requires the Gregorian expiry/suspension date; allow correction when Thiqah returns it blank */}
            <Form.Item
              labelText={t('Suspension Date – Agent Updates')}
              name={n(['agentSuspensionDate'])}
              initialValue={thiqahFullData?.status?.suspensionDate?.gregorian || undefined}
              disabled={isKsaContract}
            >
              <DateInput disabled={!detectFieldIssue(thiqahFullData?.status?.suspensionDate?.gregorian) || isKsaContract} />
            </Form.Item>
            <ReviewItem label={t('Deletion Date')} format="date">
              {thiqahFullData?.status?.deletionDate?.gregorian}
            </ReviewItem>
            {/* Applicant phone, fax, and postal code are Risk Team requirements not returned reliably by Thiqah,
                so they are always free-text entry fields for the agent */}
            <Form.Item
              labelText={t('Company Phone Number')}
              name={n(['agentApplicantPhone'])}
              initialValue={thiqahFullData?.contactInfo?.phoneNo || undefined}
              disabled={isKsaContract}
              rules={isKsaContract ? [] : [
                { required: true, message: t('Please enter company phone number') },
                { validator: validatePhoneNumber },
              ]}
            >
              <Input disabled={isKsaContract} placeholder={t('Enter company phone number')} />
            </Form.Item>
            {/* <Form.Item
              labelText={t('Fax Number – Agent Updates')}
              name={n(['agentFaxNumber'])}
              initialValue={undefined}
            >
              <Input placeholder={t('Enter fax number')} />
            </Form.Item>
            <Form.Item
              labelText={t('Postal Code – Agent Updates')}
              name={n(['agentPostalCode'])}
              initialValue={undefined}
            >
              <Input placeholder={t('Enter postal code')} />
            </Form.Item> */}
            <ReviewItem label={t('Email')}>{thiqahFullData?.contactInfo?.email}</ReviewItem>
            <Form.Item
              labelText={t('Company Mobile Number')}
              name={n(['agentMobileNumber'])}
              initialValue={elmInfo?.mobile || undefined}
              disabled={isKsaContract}
              rules={isKsaContract ? [] : [
                { required: true, message: t('Please enter company mobile number') },
                { validator: validatePhoneNumber },
              ]}
            >
              <Input disabled={isKsaContract} placeholder={t('Enter company mobile number')} />
            </Form.Item>
            {/* Applicant Mobile Number — sourced from user entry (applicantDetails.phoneNumber), not Thiqah; always disabled */}
            <Form.Item
              labelText={t('Applicant Mobile Number')}
              name={n(['applicantMobileNumber'])}
              initialValue={applicantDetails?.phoneNumber || undefined}
              disabled
            >
              <Input disabled placeholder={t('Applicant mobile number')} />
            </Form.Item>
            <Form.Item
              labelText={t('Website URL')}
              name={n(['agentWebsiteUrl'])}
              initialValue={companyInformation?.websiteUrl || undefined}
              disabled={isKsaContract}
              rules={
                isKsaContract
                  ? []
                  : [
                    ...(isEcomProduct ? [{ required: true, message: t('Please enter website URL') }] : []),
                    { validator: validateWebsite },
                  ]
              }
            >
              <Input disabled={isKsaContract} placeholder={t('Enter website URL')} />
            </Form.Item>
            {/* Electronic Website is a separate Risk Team field (e.g. e-commerce store URL) not in the applicant form */}
            {/* <Form.Item
              labelText={t('Electronic Website – Agent Updates')}
              name={n(['agentElectronicWebsite'])}
              initialValue={undefined}
            >
              <Input placeholder={t('Enter electronic website URL')} />
            </Form.Item> */}
            <Form.Item
              labelText={t('E-commerce Merchant')}
              name={n(['agentIsEcommerce'])}
              initialValue={isEcommerce}
              disabled={isKsaContract}
            >
              <Select
                placeholder={t('Select Yes or No')}
                disabled={isKsaContract}
                options={[
                  { label: t('Yes'), value: true },
                  { label: t('No'), value: false },
                ]}
              />
            </Form.Item>
            <IndentedSection>
              <SubSectionTitle level={5}>
                {t('Authentication Platforms')}
              </SubSectionTitle>
              {thiqahFullData?.eCommerce?.eStore?.length
                ? thiqahFullData.eCommerce.eStore.map((item: any, index: number) => (
                  <Tag key={index}>{item?.authenticationPlatformUrl || '-'}</Tag>
                ))
                : '-'}
            </IndentedSection>
            <IndentedSection>
              <SubSectionTitle level={5}>
                {t('Store Url')}
              </SubSectionTitle>
              {thiqahFullData?.eCommerce?.eStore?.length
                ? thiqahFullData.eCommerce.eStore.map((item: any, index: number) => (
                  <Tag key={index}>{item?.storeUrl || '-'}</Tag>
                ))
                : '-'}
            </IndentedSection>
            <IndentedSection>
              <SubSectionTitle level={5}>
                {t('Store Activities')}
              </SubSectionTitle>
              {thiqahFullData?.eCommerce?.eStore?.map((item: any) => {
                return (
                  item?.storeActivities?.map((ele: any) => <Tag key={ele}>{ele?.name}</Tag>) ?? '-'
                );
              })}
            </IndentedSection>
            {/* City [Retrieved from Thiqah] — renamed per spec */}
            <ReviewItem label={t('City [Retrieved from Thiqah]')}>
              {thiqahFullData?.headquarterCityName}
            </ReviewItem>
            {/* City is sourced from Thiqah but may come back in Arabic; only enabled for agent edit when that happens */}
            {/* <Form.Item
              labelText={t('City – Agent Updates')}
              name={n(['agentCity'])}
              initialValue={thiqahFullData?.headquarterCityName || undefined}
            >
              <Input disabled={!detectFieldIssue(thiqahFullData?.headquarterCityName)} placeholder={t('Enter city name')} />
            </Form.Item> */}

            {/* City [Merchant Submission] — NEW: from businessDetails.city (SOB), visible if has value */}
            {businessDetails?.city && (
              <ReviewItem label={t('City [Merchant Submission]')}>
                {businessDetails?.city}
              </ReviewItem>
            )}

            {/* Store Address (English) — NEW: from businessDetails.storeAddressEnglish, visible if has value */}
            {businessDetails?.storeAddressEnglish && (
              <ReviewItem label={t('Store Address (English)')}>
                {businessDetails?.storeAddressEnglish}
              </ReviewItem>
            )}

            {/* Store Address (Arabic) — NEW: from businessDetails.storeAddressArabic, visible if has value */}
            {businessDetails?.storeAddressArabic && (
              <ReviewItem label={t('Store Address (Arabic)')}>
                {businessDetails?.storeAddressArabic}
              </ReviewItem>
            )}
            {/* National Address sub-fields required by the Risk Team; not available from Thiqah so always open for agent entry */}
          </InputsGroupLayout>
        </Section>

        <Typography.Title level={4}>{t('National Address').toUpperCase()}</Typography.Title>
        <Section formItemLast>
          {requiresAddressReview && (
            <Alert
              type="warning"
              showIcon
              message={t('National address requires agent review')}
              description={t('Complete mandatory national address fields before approval.')}
              style={{ marginBottom: 16 }}
            />
          )}
          {companyInformation?.nationalAddressCertificate && (
            <ReviewItem label={t('National Address Certificate')}>
              <DocumentInput
                name="photos"
                value={companyInformation.nationalAddressCertificate}
                multiple={false}
                readonly
                itemRender={undefined}
                showUploadList={{ showRemoveIcon: false, showPreviewIcon: true }}
              />
            </ReviewItem>
          )}
          <InputsGroupLayout>
            <Form.Item
              labelText={t('National Address – Postal Code')}
              name={n(['nationalAddress', 'postalCode'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter postal code') }]}
            >
              <Input placeholder={t('Enter postal code')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Building Number')}
              name={n(['nationalAddress', 'buildingNumber'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter building number') }]}
            >
              <Input placeholder={t('Enter building number')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Additional Number')}
              name={n(['nationalAddress', 'additionalNumber'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter additional number') }]}
            >
              <Input placeholder={t('Enter additional number')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Unit Number')}
              name={n(['nationalAddress', 'unitNumber'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter unit number') }]}
            >
              <Input placeholder={t('Enter unit number')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Street Name')}
              name={n(['nationalAddress', 'streetName'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter street name') }]}
            >
              <Input placeholder={t('Enter street name')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – District Name')}
              name={n(['nationalAddress', 'districtName'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter district name') }]}
            >
              <Input placeholder={t('Enter district name')} />
            </Form.Item>
            <Form.Item
              labelText={t('Regular Address (Full Address)')}
              name={n(['nationalAddress', 'fullAddress'])}
              // disabled={isKsaContract}
              rules={[{ required: true, whitespace: true, message: t('Please enter full address') }]}
            >
              <Input placeholder={t('Enter full address')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Merchant Name')}
              name={n(['nationalAddress', 'merchantName'])}
            // disabled={isKsaContract}
            >
              <Input placeholder={t('Enter merchant name')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – City')}
              name={n(['nationalAddress', 'city'])}
            // disabled={isKsaContract}
            >
              <Input placeholder={t('Enter city')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Region Name')}
              name={n(['nationalAddress', 'regionName'])}
            // disabled={isKsaContract}
            >
              <Input placeholder={t('Enter region name')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Address ID')}
              name={n(['nationalAddress', 'addressId'])}
            // disabled={isKsaContract}
            >
              <Input placeholder={t('Enter address ID')} />
            </Form.Item>
            <Form.Item
              labelText={t('National Address – Status')}
              name={n(['nationalAddress', 'status'])}
            // disabled={isKsaContract}
            >
              <Input placeholder={t('Enter status')} />
            </Form.Item>
          </InputsGroupLayout>
        </Section >

        <Typography.Title level={4}>{t('Capital').toUpperCase()}</Typography.Title>
        <Section formItemLast>
          <InputsGroupLayout>
            {/* Risk Team needs a confirmed capital amount; editable when Thiqah returns it blank */}
            <Form.Item
              labelText={t('CR capital')}
              name={n(['agentCapitalAmount'])}
              initialValue={thiqahFullData?.crCapital || undefined}
              rules={isKsaContract ? [] : [{ required: true, message: t('Please enter capital amount') }]}
              disabled={isKsaContract}
            >
              <Input disabled={isKsaContract} placeholder={t('Enter capital amount')} />
            </Form.Item>
            <ReviewItem label={t('Contribution Capital Type')}>
              {thiqahFullData?.capital?.contributionCapital?.typeName}
            </ReviewItem>
            <ReviewItem label={t('Contribution Capital Cash')}>
              {thiqahFullData?.capital?.contributionCapital?.cashCapital}
            </ReviewItem>
            <ReviewItem label={t('Contribution Capital inKind')}>
              {thiqahFullData?.capital?.contributionCapital?.inKindCapital}
            </ReviewItem>
            <ReviewItem label={t('Contribution Value')}>
              {thiqahFullData?.capital?.contributionCapital?.contributionValue}
            </ReviewItem>
            <ReviewItem label={t('Contribution Capital Total Cash')}>
              {thiqahFullData?.capital?.contributionCapital?.totalCashContribution}
            </ReviewItem>
            <ReviewItem label={t('Contribution Capital Total inKind')}>
              {thiqahFullData?.capital?.contributionCapital?.totalInKindContribution}
            </ReviewItem>
            <ReviewItem label={t('Contribution Capital Total inKind')}>
              {thiqahFullData?.capital?.contributionCapital?.totalInKindContribution}
            </ReviewItem>
            <ReviewItem label={t('Stock Capital Type')}>
              {thiqahFullData?.capital?.stockCapital?.typeName}
            </ReviewItem>
            <ReviewItem label={t('Stock Capital Value')}>
              {thiqahFullData?.capital?.stockCapital?.capital}
            </ReviewItem>
            <ReviewItem label={t('Stock Capital Cash')}>
              {thiqahFullData?.capital?.stockCapital?.cashCapital}
            </ReviewItem>
            <ReviewItem label={t('Stock Capital InKind')}>
              {thiqahFullData?.capital?.stockCapital?.inKindCapital}
            </ReviewItem>
            <ReviewItem label={t('Announced Stock Capital')}>
              {thiqahFullData?.capital?.stockCapital?.announcedCapital}
            </ReviewItem>
            <ReviewItem label={t('Paid Stock Capital')}>
              {thiqahFullData?.capital?.stockCapital?.paidCapital}
            </ReviewItem>
            <ReviewItem label={t('Shares count')}>
              {capital?.stockCapital?.stocks?.count}
            </ReviewItem>
            <ReviewItem label={t('Shares Price')}>
              {capital?.stockCapital?.stocks?.value}
            </ReviewItem>
          </InputsGroupLayout>
        </Section>

        <Typography.Title level={4}>{t('Fiscal year').toUpperCase()}</Typography.Title>
        <Section formItemLast>
          <InputsGroupLayout>
            <Form.Item
              labelText={t('Day & Month')}
              name={n(['agentFiscalYearDayMonth'])}
              initialValue={
                thiqahFullData?.fiscalYear?.endDay && thiqahFullData?.fiscalYear?.endMonth
                  ? `${String(thiqahFullData.fiscalYear.endDay).padStart(2, '0')}/${String(thiqahFullData.fiscalYear.endMonth).padStart(2, '0')}`
                  : undefined
              }
              disabled={isKsaContract}
              rules={isKsaContract ? [] : [{ required: true, message: t('Please enter the day & month') }]}
              getValueProps={(val) => ({ value: val ? dayjs(`2000/${val}`, 'YYYY/DD/MM') : undefined })}
              normalize={(val: dayjs.Dayjs | null) => val ? val.format('DD/MM') : undefined}
            >
              <FullWidthDatePicker
                format="DD/MM"
                size="large"
                disabled={isKsaContract}
                placeholder={t('DD/MM')}
              />
            </Form.Item>
            {/* Calendar type (Hijri / Gregorian) is required by Risk Team; correct it if Thiqah returns it in Arabic */}
            <Form.Item
              labelText={t('Calendar Type')}
              name={n(['agentCalendarType'])}
              initialValue={thiqahFullData?.fiscalYear?.calendarTypeName || undefined}
              disabled={isKsaContract}
              rules={isKsaContract ? [] : [{ required: true, message: t('Please enter the calendar type') }]}
            >
              <Input disabled={isKsaContract} placeholder={t('Enter calendar type')} />
            </Form.Item>
          </InputsGroupLayout>
        </Section>
        <HiddenSection>
          <Typography.Title level={4}>{t('Activities').toUpperCase()}</Typography.Title>
          <Section formItemLast>
            <ReviewItem label={t('Main activity')}>
              {thiqahFullData?.activities?.[0]?.name}
            </ReviewItem>
            <ReviewItem label={t('ISIC')}>{thiqahFullData?.storeActivities?.[0]?.name}</ReviewItem>
          </Section>
        </HiddenSection>
        <Typography.Title level={4}>{t('Main business nature').toUpperCase()}</Typography.Title>
        <Section formItemLast>
          <InputsGroupLayout>
            <Form.Item
              labelText={t('Main Business Activity')}
              name={p(['mccGroup'])}
              initialValue={companyInformation?.mccGroup}
              disabled={isKsaContract || !!companyInformation?.mccGroup}
            >
              <ActivityListWrapper>
                <ActivitySearchBox
                  placeholder={t('Search activities...')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setOptionPage(0);
                  }}
                />
                <ActivityList>
                  {visibleOptions.length > 0 ? (
                    visibleOptions.map((opt, idx) => (
                      <ActivityListItem key={idx}>{opt}</ActivityListItem>
                    ))
                  ) : (
                    <ActivityListItem style={{ color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
                      {t('No results found')}
                    </ActivityListItem>
                  )}
                </ActivityList>
                {filteredOptions.length > PAGE_SIZE && (
                  <ActivityPagination>
                    <NavButton
                      type="button"
                      disabled={optionPage === 0}
                      onClick={() => setOptionPage((prev) => Math.max(0, prev - 1))}
                    >
                      &#8592; {t('Previous')}
                    </NavButton>
                    <PageIndicator>
                      {optionPage * PAGE_SIZE + 1}–{Math.min((optionPage + 1) * PAGE_SIZE, filteredOptions.length)}
                      {' '}{t('of')}{' '}{filteredOptions.length}
                    </PageIndicator>
                    <NavButton
                      type="button"
                      disabled={optionPage >= totalOptionPages - 1}
                      onClick={() => setOptionPage((prev) => Math.min(totalOptionPages - 1, prev + 1))}
                    >
                      {t('Next')} &#8594;
                    </NavButton>
                  </ActivityPagination>
                )}
              </ActivityListWrapper>
            </Form.Item>
            <Form.Item
              label={t('Business nature (MCC code)')}
              name={p(['mccCode'])}
              initialValue={companyInformation?.mccCode}
            >
              <MccCodeSelect withCode />
            </Form.Item>
            <Form.Item
              label={t('Master MID')}
              name={p(['masterId'])}
              initialValue={companyInformation?.masterId}
              disabled={isKsaContract}
            >
              <Input disabled={isKsaContract} />
            </Form.Item>
            <Form.Item
              label={t('Master TID')}
              name={p(['terminalId'])}
              initialValue={companyInformation?.terminalId}
              disabled={isKsaContract}
            >
              <Input disabled={isKsaContract} />
            </Form.Item>
          </InputsGroupLayout>
        </Section>
        <Typography.Title level={4}>
          {t('Tax registration certificate').toUpperCase()}
        </Typography.Title>
        <Section formItemLast>
          <ReviewItem label={t('Tax Registration Number')}>
            {companyInformation?.taxRegistrationCertificate?.number}
          </ReviewItem>
        </Section>
        <Typography.Title level={4}>
          {t('Risk Questionnaire').toUpperCase()}
        </Typography.Title>
        <Section formItemLast>
          <InputsGroupLayout>
            <ReviewItem label={t('Expected number of transactions per month')}>
              {getRqLocalizedValue('monthlyTransactionCount', riskQuestionnaire?.monthlyTransactionCount)}
            </ReviewItem>

            <ReviewItem label={t('Expected monthly sales amount ({SAR})')}>
              {getRqLocalizedValue('monthlySalesAmount', riskQuestionnaire?.monthlySalesAmount)}
            </ReviewItem>

            <ReviewItem label={t('Expected average transaction value ({SAR})')}>
              {getRqLocalizedValue('averageTransactionValue', riskQuestionnaire?.averageTransactionValue)}
            </ReviewItem>

            <ReviewItem label={t('Expected annual revenue ({SAR})')}>
              {getRqLocalizedValue('annualRevenue', riskQuestionnaire?.annualRevenue)}
            </ReviewItem>
          </InputsGroupLayout>
        </Section>
        <Section>
          <StepReviewComment n={n} />
        </Section>
      </WizardForm >
    </Container >
  );
};

const Container = styled.div`
  .ant-typography ul {
    list-style-type: disc;
  }
`;

const IndentedSection = styled.div`
  padding-left: 24px;
`;

const SubSectionTitle = styled(Typography.Title)`
  margin-bottom: 8px !important;
`;

const HiddenSection = styled.div`
  display: none;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const ActivityListWrapper = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
`;

const ActivitySearchBox = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e8e8e8;
  outline: none;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  background: #fafafa;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(0, 0, 0, 0.35);
  }

  &:focus {
    background: #fff;
    border-bottom-color: #1677ff;
  }
`;

const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ActivityListItem = styled.li`
  padding: 8px 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  border-bottom: 1px solid #f0f0f0;
  cursor: default;
  user-select: none;

  &:last-child {
    border-bottom: none;
  }
`;

const ActivityPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-top: 1px solid #e8e8e8;
  background: #fafafa;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 12px;
  cursor: pointer;
  color: #1677ff;
  transition: background 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background: #e6f4ff;
    border-color: #1677ff;
  }

  &:disabled {
    color: rgba(0, 0, 0, 0.25);
    border-color: #d9d9d9;
    cursor: not-allowed;
  }
`;

const PageIndicator = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

export default CompanyInformation;
